import React, { useEffect, useMemo, useState } from "react";
import { useLive } from "@/lib/live";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { HardHat, CurrencyInr, CheckCircle, Clock, Receipt, ArrowClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { istToday, istDate } from "@/lib/istDate";

const inr = (n) => `₹${(Number(n) || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const todayStr = () => istToday();
const fmtDate = (s) => {
  if (!s) return "";
  try {
    return new Date(s.length === 10 ? s + "T00:00:00" : s)
      .toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch (_e) { return s; }
};

export default function WelderCommissionsPage() {
  const { user } = useAuth();
  const canUse = user?.role === "admin" || user?.role === "secondary_admin";

  const monthStart = () => {
    const d = new Date();
    return istDate(new Date(d.getFullYear(), d.getMonth(), 1));
  };
  const [fromDate, setFromDate] = useState(monthStart());
  const [toDate, setToDate] = useState(todayStr());
  const [status, setStatus] = useState("unpaid");
  const [data, setData] = useState({ pending_total: 0, paid_total: 0, by_welder: [], rows: [] });
  const [loading, setLoading] = useState(true);
  const [payRow, setPayRow] = useState(null);
  const [accounts, setAccounts] = useState([]);

  useLive(() => { load(); });
  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/welder-commissions?from=${fromDate}&to=${toDate}&status=${status}`);
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Load fail hua");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data: accts } = await api.get("/accounts/financial-accounts");
        setAccounts((accts || []).filter((a) => a.is_active !== false));
      } catch (e) { /* ignore */ }
    })();
  }, []);

  useEffect(() => { if (canUse) load(); /* eslint-disable-next-line */ }, [fromDate, toDate, status, canUse]);

  const quickRanges = useMemo(() => {
    const d = new Date();
    const iso = (dt) => istDate(dt);
    const today = iso(d);
    const weekStart = iso(new Date(d.getFullYear(), d.getMonth(), d.getDate() - 6));
    const monStart = iso(new Date(d.getFullYear(), d.getMonth(), 1));
    const prevMonStart = iso(new Date(d.getFullYear(), d.getMonth() - 1, 1));
    const prevMonEnd = iso(new Date(d.getFullYear(), d.getMonth(), 0));
    const yearStart = iso(new Date(d.getFullYear(), 0, 1));
    return [
      { label: "Today", from: today, to: today },
      { label: "This Week", from: weekStart, to: today },
      { label: "This Month", from: monStart, to: today },
      { label: "Last Month", from: prevMonStart, to: prevMonEnd },
      { label: "This Year", from: yearStart, to: today },
    ];
  }, []);

  if (!canUse) {
    return <div className="p-10 text-center text-slate-400">Welder Commissions access sirf admin / secondary admin ke liye hai.</div>;
  }

  return (
    <div className="space-y-5" data-testid="welder-commissions-page">
      <div>
        <div className="text-xs font-mono-num tracking-widest text-slate-400">// EARNINGS</div>
        <h1 className="font-heading text-3xl font-black text-white">Welder Commissions</h1>
        <p className="text-slate-400 text-sm mt-1">
          Jab order lete waqt <b className="text-amber-300">Welder Commission</b> toggle on karte ho, wo amount yahaan pending me aata hai.
          Welder ko cash dete waqt row ke <b className="text-emerald-300">PAY</b> button pe tap karo — Cash account se auto-debit ho jayega.
        </p>
      </div>

      {/* Date range */}
      <div className="rounded-sm border border-slate-800 bg-slate-900/40 p-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[140px]">
          <Label className="text-xs text-slate-400 tracking-widest">FROM</Label>
          <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
            className="rounded-sm bg-slate-950 border-slate-700 h-10 font-mono-num" data-testid="wc-from"/>
        </div>
        <div className="flex-1 min-w-[140px]">
          <Label className="text-xs text-slate-400 tracking-widest">TO</Label>
          <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
            className="rounded-sm bg-slate-950 border-slate-700 h-10 font-mono-num" data-testid="wc-to"/>
        </div>
        <div className="flex flex-wrap gap-2">
          {quickRanges.map((r) => (
            <Button key={r.label} variant="outline" size="sm"
              onClick={() => { setFromDate(r.from); setToDate(r.to); }}
              className="rounded-sm h-9 border-slate-700 text-slate-300 hover:bg-slate-800 text-xs tracking-wider"
              data-testid={`wc-range-${r.label.toLowerCase().replace(/\s+/g, '-')}`}>
              {r.label}
            </Button>
          ))}
        </div>
        <div>
          <Label className="text-xs text-slate-400 tracking-widest">STATUS</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-36 rounded-sm bg-slate-950 border-slate-700 h-10" data-testid="wc-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-sm bg-slate-900 border-slate-700">
              <SelectItem value="unpaid">Pending</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="rounded-sm border border-amber-500/40 bg-amber-500/5 p-5">
          <div className="flex items-center gap-2">
            <Clock size={16} className="text-amber-300" />
            <div className="text-xs tracking-widest text-amber-300 font-mono-num">PENDING</div>
          </div>
          <div className="font-mono-num text-3xl font-black text-amber-200 mt-2" data-testid="wc-pending-total">{inr(data.pending_total)}</div>
          <div className="text-xs text-slate-400 mt-1">Un-settled welder dues</div>
        </div>
        <div className="rounded-sm border border-emerald-500/40 bg-emerald-500/5 p-5">
          <div className="flex items-center gap-2">
            <CheckCircle size={16} className="text-emerald-300" />
            <div className="text-xs tracking-widest text-emerald-300 font-mono-num">PAID</div>
          </div>
          <div className="font-mono-num text-3xl font-black text-emerald-200 mt-2" data-testid="wc-paid-total">{inr(data.paid_total)}</div>
          <div className="text-xs text-slate-400 mt-1">Already settled</div>
        </div>
        <div className="rounded-sm border border-slate-800 bg-slate-900/50 p-5">
          <div className="flex items-center gap-2 mb-2">
            <HardHat size={16} className="text-slate-400" />
            <div className="text-xs tracking-widest text-slate-400 font-mono-num">BY WELDER (PENDING)</div>
          </div>
          {(data.by_welder || []).filter((w) => w.pending > 0).length === 0 ? (
            <div className="text-slate-500 text-sm">Koi pending nahi 🎉</div>
          ) : (
            <div className="space-y-1 max-h-32 overflow-auto">
              {data.by_welder.filter((w) => w.pending > 0).map((w) => (
                <div key={w.welder_id || w.welder_name}
                  className="flex items-center justify-between text-xs"
                  data-testid={`wc-welder-${w.welder_id || w.welder_name}`}
                >
                  <span className="text-slate-200 truncate">{w.welder_name}</span>
                  <span className="font-mono-num text-amber-200 font-bold shrink-0 ml-2">{inr(w.pending)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Rows */}
      <div className="rounded-sm border border-slate-800 overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-4 py-2 text-[10px] font-mono-num tracking-widest text-slate-400 bg-slate-900/60 border-b border-slate-800">
          <div className="col-span-2">DATE</div>
          <div className="col-span-2">ORDER #</div>
          <div className="col-span-3">WELDER</div>
          <div className="col-span-2">CUSTOMER</div>
          <div className="col-span-2 text-right">AMOUNT</div>
          <div className="col-span-1 text-right">ACTION</div>
        </div>
        {loading ? (
          <div className="p-6 text-center text-slate-400 font-mono-num text-sm">Loading…</div>
        ) : data.rows.length === 0 ? (
          <div className="p-10 text-center">
            <Receipt size={40} className="mx-auto text-slate-600 mb-2" />
            <div className="text-slate-400 text-sm">Is period mein koi welder commission entry nahi hai.</div>
          </div>
        ) : (
          data.rows.map((r) => (
            <div key={r.order_id}
              className={"grid grid-cols-12 gap-2 px-4 py-3 border-b border-slate-800 last:border-b-0 items-center text-sm " +
                (r.paid ? "bg-emerald-500/5" : "")}
              data-testid={`wc-row-${r.order_number}`}
            >
              <div className="col-span-2 font-mono-num text-slate-200">{fmtDate(r.created_at)}</div>
              <div className="col-span-2 font-mono-num text-slate-100">#{r.order_number}</div>
              <div className="col-span-3">
                <div className="text-slate-100 font-bold truncate">{r.welder_name}</div>
                <div className="text-[11px] text-slate-500">{r.welder_mobile || "—"}</div>
                {r.note && <div className="text-[10px] text-slate-400 italic mt-0.5">{r.note}</div>}
              </div>
              <div className="col-span-2 text-slate-300 truncate">{r.customer_name}</div>
              <div className="col-span-2 text-right font-mono-num text-amber-200 font-black">{inr(r.amount)}</div>
              <div className="col-span-1 text-right">
                {r.paid ? (
                  <span className="inline-flex items-center gap-1 text-[10px] font-mono-num text-emerald-300" title={`Paid ${fmtDate(r.paid_at)}`}>
                    <CheckCircle size={12} weight="fill" /> PAID
                  </span>
                ) : (
                  <Button size="sm"
                    onClick={() => setPayRow(r)}
                    className="h-7 px-3 rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white font-mono-num text-xs tracking-wider"
                    data-testid={`wc-pay-${r.order_number}`}
                  >PAY</Button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <PayDialog
        row={payRow}
        onClose={() => setPayRow(null)}
        accounts={accounts}
        onPaid={() => { setPayRow(null); load(); }}
      />
    </div>
  );
}

function PayDialog({ row, onClose, accounts, onPaid }) {
  const [accId, setAccId] = useState("");
  const [method, setMethod] = useState("Cash");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (row) {
      setAccId(accounts.find((a) => a.account_type === "cash")?.id || accounts[0]?.id || "");
      setMethod("Cash");
      setNote(`Welder commission for #${row.order_number}`);
    }
  }, [row, accounts]);

  const pay = async () => {
    if (!accId) { toast.error("Account chuno"); return; }
    setBusy(true);
    try {
      await api.post(`/welder-commissions/${row.order_id}/pay`, {
        financial_account_id: accId,
        payment_method: method,
        note: note.trim(),
      });
      toast.success(`${row.welder_name} ko ₹${row.amount} pay ho gaya`);
      onPaid();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Pay fail hua");
    } finally { setBusy(false); }
  };

  if (!row) return null;
  return (
    <Dialog open={!!row} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm border-slate-800 bg-slate-950 max-w-md" data-testid="wc-pay-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-white flex items-center gap-2">
            <CurrencyInr size={20} weight="fill" className="text-emerald-400" /> Pay Welder Commission
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-sm bg-amber-500/10 border border-amber-500/30 p-3 space-y-1">
            <div className="text-sm text-slate-200"><b>{row.welder_name}</b> {row.welder_mobile ? `· ${row.welder_mobile}` : ""}</div>
            <div className="text-xs text-slate-400">Order #{row.order_number} · {row.customer_name}</div>
            <div className="font-mono-num text-3xl font-black text-amber-200 pt-1">₹{Number(row.amount).toLocaleString("en-IN")}</div>
          </div>
          <div>
            <Label className="text-xs text-slate-400 tracking-widest">PAY FROM</Label>
            <Select value={accId} onValueChange={setAccId}>
              <SelectTrigger className="rounded-sm bg-slate-900 border-slate-700 h-10" data-testid="wc-pay-acc">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-sm bg-slate-900 border-slate-700">
                {accounts.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.account_name} <span className="text-slate-500 text-xs">· {a.account_type}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-400 tracking-widest">METHOD</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger className="rounded-sm bg-slate-900 border-slate-700 h-10"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-sm bg-slate-900 border-slate-700">
                {["Cash", "UPI", "Bank Transfer", "Cheque", "Card", "Other"].map((m) => (
                  <SelectItem key={m} value={m}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-400 tracking-widest">NOTE</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)}
              className="rounded-sm bg-slate-900 border-slate-700 h-10"
              data-testid="wc-pay-note"/>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose}
              className="rounded-sm border-slate-700 text-slate-300 hover:bg-slate-800">
              Cancel
            </Button>
            <Button onClick={pay} disabled={busy}
              className="rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white font-mono-num tracking-wider disabled:opacity-60"
              data-testid="wc-pay-confirm"
            >
              {busy ? "Paying…" : `CONFIRM ₹${row.amount}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
